/** The one place the portal talks to the service.
 *
 * Every request is same-origin and carries the session cookie — the portal is
 * served by the Worker that answers `/api`, so there is no base URL to
 * configure and no bearer token anywhere in this file. Tokens are for loggers,
 * not for people: the portal creates them, shows one once, and never sends
 * one.
 *
 * The service answers failures as `{ error, reason }` (see `src/lib/http.ts`),
 * and that is turned into a thrown `ApiError` carrying the status, the code
 * and the reason. The reason is written to be read by a person, so a screen
 * can put it on the page as it stands; the code is what to branch on. Two
 * statuses mean something specific to the portal and are worth knowing:
 * `401` is "not signed in" rather than "something went wrong", which is why
 * `getMe` is allowed to come back null, and `403 type_disabled` is the server
 * saying a whole data type is switched off.
 *
 * Below the transport is one named function per endpoint, each typed against
 * `lib/types.ts`. A screen imports the function it needs; nothing outside this
 * file builds a URL or reads a status code.
 */

import type { LapAnalysis } from '@/components/sessions/analysis';
import type {
  AccountStateChanged,
  AdminAccountPage,
  AdminAccountQuery,
  ActivityPage,
  ActivityQuery,
  AdminAuditPage,
  AdminAuditQuery,
  AdminCosts,
  AdminDashboard,
  AdminLiveListing,
  AdminLogPage,
  AdminLogQuery,
  AdminSessionPage,
  AdminSessionQuery,
  AdminSettings,
  AdminUploadPage,
  AdminUploadQuery,
  Capabilities,
  CreateToken,
  CreatedToken,
  DisableUser,
  DriveSession,
  IssueDetail,
  IssueList,
  IssueQuery,
  IssueTypeDefinition,
  JobStarted,
  SweepRun,
  KickedStream,
  LapDocument,
  List,
  LiveListing,
  LiveStream,
  Me,
  MergeRequestList,
  MergeRequestQuery,
  OpenIssueCount,
  Provider,
  ProviderList,
  PublicDriverList,
  PurgedSession,
  ResolveIssue,
  ResolvedIssue,
  RotatedServiceKey,
  RunJob,
  ServerSettings,
  ServiceMeta,
  SessionDetail,
  SessionListReply,
  SessionQuery,
  SettingsWritten,
  SharedSessionReply,
  SetUploadInterval,
  SetUploadStatus,
  Source,
  Track,
  TrackCandidate,
  TrackEditSource,
  TrackEditSubmitted,
  TrackHistory,
  TrackList,
  TrackOutline,
  TrackQuery,
  TrackStatus,
  TokenSummary,
  UpdateMe,
  UpdateSession,
  Upload,
  UploadIntervalChanged,
  UploadTimeline,
  UploadTimelineRange,
  UploadQuery,
  UploadStatusChanged,
} from '@/lib/types';

/** A failure the service described. Never thrown for a network drop — that
 *  surfaces as the underlying `TypeError`, which is a different problem and
 *  deserves different words on screen. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly reason: string;

  constructor(status: number, code: string, reason: string) {
    super(reason || code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.reason = reason;
  }

  /** The visitor is not signed in. Not an error on a public page. */
  get unauthenticated(): boolean {
    return this.status === 401;
  }

  /** The server is not accepting this data type at all. */
  get typeDisabled(): boolean {
    return this.status === 403 && this.code === 'type_disabled';
  }
}

/** Anything thrown out of this module, in words a screen can print. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.reason || error.code;
  if (error instanceof Error) return error.message || 'the request did not complete';
  return 'the request did not complete';
}

type Query = Record<string, string | number | boolean | undefined | null>;

/** A query string with the untouched keys left out, so `?limit=50` never
 *  turns into `?limit=50&status=undefined`. */
function qs(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  };
  if (body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }

  const response = await fetch(path, init);

  // 204, and any empty body, is a successful nothing.
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    if (!response.ok) throw new ApiError(response.status, 'http_error', response.statusText);
    return undefined as T;
  }

  const text = await response.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // A non-JSON body from an endpoint that promises JSON is the Worker
      // failing before it reached a handler, or an asset route swallowing the
      // path. Either way the status is the only fact worth reporting.
      if (!response.ok) {
        throw new ApiError(response.status, 'http_error', response.statusText || 'request failed');
      }
      throw new ApiError(response.status, 'bad_reply', 'the server sent something that is not JSON');
    }
  }

  if (!response.ok) {
    const shape = (parsed ?? {}) as { error?: unknown; reason?: unknown };
    const code = typeof shape.error === 'string' ? shape.error : 'http_error';
    const reason =
      typeof shape.reason === 'string' && shape.reason
        ? shape.reason
        : response.statusText || 'request failed';
    throw new ApiError(response.status, code, reason);
  }

  return parsed as T;
}

/** The transport, for anything the named functions below do not cover. */
export const api = {
  get: <T>(path: string, query?: Query): Promise<T> => request<T>('GET', path + qs(query)),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body ?? {}),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body ?? {}),
  del: <T>(path: string): Promise<T> => request<T>('DELETE', path),
};

// ── sign in ───────────────────────────────────────────────────────────────

/** Which sign-in buttons to draw. Public by necessity — nobody calling it is
 *  signed in — and asked rather than assumed, because the admin can switch a
 *  provider on without a redeploy. */
export const getProviders = (): Promise<ProviderList> =>
  api.get<ProviderList>('/api/providers');

/** Who this deployment is and where its links point: the donate URL, the
 *  track-data repo, the version in a bug report. */
export const getMeta = (): Promise<ServiceMeta> => api.get<ServiceMeta>('/api/meta');

/** Where the "Sign in with …" buttons point. A full navigation, not a fetch:
 *  the provider redirects the browser back to the callback, which sets the
 *  cookie. `link` adds a second provider to the account already signed in. */
export function authStartUrl(
  provider: Provider | string,
  options: { redirectTo?: string; link?: boolean } = {},
): string {
  return (
    `/auth/${encodeURIComponent(provider)}/start` +
    qs({ redirect_to: options.redirectTo, link: options.link ? 1 : undefined })
  );
}

export const signOut = (): Promise<void> => api.post<void>('/auth/signout');

// ── the account ───────────────────────────────────────────────────────────

export const getMe = (): Promise<Me> => api.get<Me>('/api/me');

/** The number beside the Admin link in the nav. Its own endpoint rather than
 *  a field of `/api/me`, which does not carry one: `src/routes/public.ts`
 *  answers this from the same grouped count the admin dashboard uses, so the
 *  two badges on an admin screen cannot disagree. Admins only. */
export const getOpenIssueCount = (): Promise<OpenIssueCount> =>
  api.get<OpenIssueCount>('/api/issues/count');

export const patchMe = (body: UpdateMe): Promise<Me> => api.patch<Me>('/api/me', body);

/** Revokes every token and anonymises the identities. Source ids stay bound,
 *  so they can never be re-claimed, and merged survey data stays in the map. */
export const deleteMe = (): Promise<void> => api.del<void>('/api/me');

// ── tokens and installations ──────────────────────────────────────────────

export const listTokens = (): Promise<List<TokenSummary>> =>
  api.get<List<TokenSummary>>('/api/tokens');

/** The only reply with a secret in it. Show it once and let it go. */
export const createToken = (body: CreateToken): Promise<CreatedToken> =>
  api.post<CreatedToken>('/api/tokens', body);

export const revokeToken = (id: string): Promise<void> =>
  api.del<void>(`/api/tokens/${encodeURIComponent(id)}`);

export const listSources = (): Promise<List<Source>> => api.get<List<Source>>('/api/sources');

export const patchSource = (sourceId: string, body: { label: string }): Promise<Source> =>
  api.patch<Source>(`/api/sources/${encodeURIComponent(sourceId)}`, body);

export const listUploads = (query?: UploadQuery): Promise<List<Upload>> =>
  api.get<List<Upload>>('/api/uploads', query as Query);

// ── tracks, public ────────────────────────────────────────────────────────

export const getCapabilities = (): Promise<Capabilities> =>
  api.get<Capabilities>('/v1/capabilities');

export const listTracks = (query?: TrackQuery): Promise<TrackList> =>
  api.get<TrackList>('/v1/tracks', query as Query);

/** One circuit, with its compiled geometry attached. */
export const getTrack = (officialId: string): Promise<Track> =>
  api.get<Track>(`/v1/tracks/${encodeURIComponent(officialId)}`);

/** The outline a card draws, at a sixth of the full document. The Tracks
 *  page asks for one per published circuit once its list is in. */
export const getTrackOutline = (officialId: string): Promise<TrackOutline> =>
  api.get<TrackOutline>(`/v1/tracks/${encodeURIComponent(officialId)}/outline`);

/** Who surveyed a circuit, and when each survey was merged. */
export const getTrackHistory = (officialId: string): Promise<TrackHistory> =>
  api.get<TrackHistory>(`/v1/tracks/${encodeURIComponent(officialId)}/history`);

export const getTrackStatus = (officialId: string): Promise<TrackStatus> =>
  api.get<TrackStatus>(`/v1/tracks/status/${encodeURIComponent(officialId)}`);

// ── the driver's own sessions ─────────────────────────────────────────────

export const listSessions = (query?: SessionQuery): Promise<SessionListReply> =>
  api.get<SessionListReply>('/api/sessions', query as Query);

/**
 * Share links this page has opened, by session id.
 *
 * A session shared by link is read through the same three endpoints as any
 * other, with its token as `?share=`. The token is remembered here when the
 * shared page resolves it, rather than threaded through every hook between
 * that page and the request, because those hooks — the lap documents, the
 * analysis, the reference lap — are the ones the Analysis page already uses
 * for a driver's own sessions, and a session id is the only thing they know.
 * It is keyed by that id, so a token is only ever sent for the session it
 * opens, and it lives as long as the page does.
 */
const shareTokens = new Map<string, string>();

export function rememberShare(sessionId: string, token: string): void {
  shareTokens.set(sessionId, token);
}

function shareQuery(id: string): Query | undefined {
  const token = shareTokens.get(id);
  return token ? { share: token } : undefined;
}

export const getSession = (id: string): Promise<SessionDetail> =>
  api.get<SessionDetail>(`/api/sessions/${encodeURIComponent(id)}`, shareQuery(id));

/** One lap document, as it was stored. Large, immutable once written, and
 *  fetched only when a driver clicks the lap — never for a whole session. */
export const getSessionLap = (id: string, lap: number): Promise<LapDocument> =>
  api.get<LapDocument>(`/api/sessions/${encodeURIComponent(id)}/laps/${lap}`, shareQuery(id));

/** A session's lap analysis, the logger's own document as it was stored. */
export const getSessionAnalysis = (id: string): Promise<LapAnalysis> =>
  api.get<LapAnalysis>(`/api/sessions/${encodeURIComponent(id)}/analysis`, shareQuery(id));

/** What a share link opens: the session and whose drive it is. Public — the
 *  people a link is sent to are mostly not signed in. */
export const getShared = (token: string): Promise<SharedSessionReply> =>
  api.get<SharedSessionReply>(`/api/shared/${encodeURIComponent(token)}`);

/** The address a share link is, on this deployment. */
export function shareUrl(token: string): string {
  return `${window.location.origin}/s/${encodeURIComponent(token)}`;
}

/** The preview card a share link unfurls into. */
export function shareCardUrl(token: string): string {
  return `/s/${encodeURIComponent(token)}/card.png`;
}

/** What a driver can change about a stored session: who may see it, the
 *  unit its preview card prints, and whether every lap's recording is kept
 *  past the lap retention. */
export const patchSession = (
  id: string,
  body: UpdateSession,
): Promise<{ session: DriveSession }> =>
  api.patch<{ session: DriveSession }>(`/api/sessions/${encodeURIComponent(id)}`, body);

export const deleteSession = (id: string): Promise<void> =>
  api.del<void>(`/api/sessions/${encodeURIComponent(id)}`);

/** Other drivers' public laps on the same circuit — what "compare with"
 *  offers. Only accounts that marked a session public appear here. */
export const listPublicLaps = (officialId: string): Promise<PublicDriverList> =>
  api.get<PublicDriverList>('/api/sessions/public', { official_id: officialId });

// ── live ──────────────────────────────────────────────────────────────────

export const listLive = (): Promise<List<LiveStream>> => api.get<List<LiveStream>>('/api/live');

/** The same endpoint in the shape it actually answers. `/api/live` returns
 *  `{ enabled, streams }` rather than the collection envelope, because the
 *  server's live toggle is part of the answer: a spectate page has to tell
 *  "nobody is driving" from "this service is not carrying live streams". */
export const getLiveListing = (): Promise<LiveListing> => api.get<LiveListing>('/api/live');

/**
 * Where a spectator watches one account drive.
 *
 * Keyed by the account id, because that is what `GET /v1/live/{user}` looks
 * up — `SELECT ... FROM users WHERE id = ?` — and what `GET /api/me` puts in
 * the `spectate_url` it hands out. A display name is nicer to read and is not
 * a key: it is not unique in the schema, it changes when somebody renames
 * themselves, and the handler would answer `no such account` for every one of
 * them.
 */
export function spectatePath(userId: string): string {
  return `/live/${encodeURIComponent(userId)}`;
}

/** The spectate socket. Same origin, so the scheme follows the page's: a
 *  local `http` dev server speaks `ws`, the deployed `https` one `wss`. The
 *  segment is an account id, for the reason `spectatePath` gives. */
export function liveSocketUrl(userId: string): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/v1/live/${encodeURIComponent(userId)}`;
}

// ── admin ─────────────────────────────────────────────────────────────────

export const adminDashboard = (): Promise<AdminDashboard> =>
  api.get<AdminDashboard>('/api/admin/dashboard');

/** Every upload across every account, with the contributor beside each one
 *  and the statuses an admin may move it to. */
export const adminUploads = (query?: AdminUploadQuery): Promise<AdminUploadPage> =>
  api.get<AdminUploadPage>('/api/admin/uploads', query as Query);

/** Force `held → pending`, reject with a note, or put one back in the queue.
 *  The reply is only what moved — the screen already has the rest of the
 *  row — and a rejection without a reason is refused with `reason_required`. */
export const setUploadStatus = (
  id: string,
  body: SetUploadStatus,
): Promise<UploadStatusChanged> =>
  api.post<UploadStatusChanged>(`/api/admin/uploads/${encodeURIComponent(id)}/status`, body);

export const adminUsers = (query?: AdminAccountQuery): Promise<AdminAccountPage> =>
  api.get<AdminAccountPage>('/api/admin/users', query as Query);

/**
 * Disable an account, or lift a disable.
 *
 * Two endpoints on the server — `/disable` and `/enable` — and the body's
 * `disabled` flag decides which one is called. It has to: the handlers do not
 * read the flag, so a call to `/disable` carrying `{ disabled: false }` would
 * disable the account it was asked to enable. Disabling refuses further
 * uploads and nothing else; merged data stays. The reason goes to the audit
 * row, not to the account.
 */
export const disableUser = (id: string, body: DisableUser): Promise<AccountStateChanged> =>
  api.post<AccountStateChanged>(
    `/api/admin/users/${encodeURIComponent(id)}/${body.disabled ? 'disable' : 'enable'}`,
    body.reason ? { reason: body.reason } : {},
  );

/** Track uploads counted over time, for the graph on the Logs screen. */
export const adminUploadTimeline = (range: UploadTimelineRange): Promise<UploadTimeline> =>
  api.get<UploadTimeline>('/api/admin/uploads/timeline', { range });

/**
 * Give one account an upload interval of its own, or — with `interval_s:
 * null` — put it back on the server's. It changes how long that account waits
 * between track uploads and nothing else.
 */
export const setUploadInterval = (
  id: string,
  body: SetUploadInterval,
): Promise<UploadIntervalChanged> =>
  api.post<UploadIntervalChanged>(
    `/api/admin/users/${encodeURIComponent(id)}/upload-interval`,
    body.reason ? { interval_s: body.interval_s, reason: body.reason } : { interval_s: body.interval_s },
  );

/** The page carries the gate thresholds as well as the rows, so the cards
 *  under the table restate the policy that was actually applied. */
export const adminMergeRequests = (query?: MergeRequestQuery): Promise<MergeRequestList> =>
  api.get<MergeRequestList>('/api/admin/merge-requests', query as Query);

/** What the pull request waiting on a circuit would publish. `404` is
 *  ordinary: nothing is waiting, or the job has not sent one yet. */
export const adminTrackCandidate = (officialId: string): Promise<TrackCandidate> =>
  api.get<TrackCandidate>(`/api/admin/tracks/${encodeURIComponent(officialId)}/candidate`);

// ── the track editor ───────────────────────────────────────────────────────

/** Which circuit the editor has opened, and what edits of it are about. */
export const adminTrackSource = (officialId: string): Promise<TrackEditSource> =>
  api.get<TrackEditSource>(`/api/admin/tracks/${encodeURIComponent(officialId)}/source`);

/**
 * The circuit's bundle, or its corrections, as `main` holds them — as TEXT.
 *
 * Text and not a parsed document, because an edit has to say which
 * corrections it started from and says so with a digest of these exact bytes;
 * re-serialising a parsed copy would be a digest of something else. `null` is
 * a `404`, which for corrections is ordinary: most circuits have none.
 */
export async function adminTrackSourceText(
  officialId: string,
  which: 'bundle' | 'corrections',
): Promise<string | null> {
  const response = await fetch(
    `/api/admin/tracks/${encodeURIComponent(officialId)}/source/${which}`,
    { credentials: 'include', headers: { Accept: 'application/json' } },
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    let reason = response.statusText || 'request failed';
    try {
      const body = (await response.json()) as { reason?: unknown };
      if (typeof body.reason === 'string' && body.reason) reason = body.reason;
    } catch {
      // The status is the only fact worth reporting.
    }
    throw new ApiError(response.status, 'http_error', reason);
  }
  return response.text();
}

/** Submit an edit. The reply is a `202`: a pull request follows, not a map. */
export const submitTrackEdit = (officialId: string, edit: unknown): Promise<TrackEditSubmitted> =>
  api.post<TrackEditSubmitted>(`/api/admin/tracks/${encodeURIComponent(officialId)}/edits`, edit);

/** Run the merge job now, for one circuit or all of them. */
export const runJob = (body: RunJob = {}): Promise<JobStarted> =>
  api.post<JobStarted>('/api/admin/job/run', body);

/** The nightly sweep, now. Answers when it has finished, which takes seconds. */
export const runSweep = (): Promise<SweepRun> => api.post<SweepRun>('/api/admin/sweep/run');

export const adminIssues = (query?: IssueQuery): Promise<IssueList> =>
  api.get<IssueList>('/api/admin/issues', query as Query);

export const adminIssue = (id: string): Promise<IssueDetail> =>
  api.get<IssueDetail>(`/api/admin/issues/${encodeURIComponent(id)}`);

/** Every issue type, what raises it and what may be done about it. The admin
 *  panel renders its resolve buttons from this rather than from a list
 *  written into the portal, so a resolution added to the domain appears on
 *  the screen without this file being touched. A listing already carries the
 *  same table; this is for a screen that needs it without one. */
export const adminIssueTypes = (): Promise<{ types: Record<string, IssueTypeDefinition> }> =>
  api.get<{ types: Record<string, IssueTypeDefinition> }>('/api/admin/issues/resolutions');

/** Applies the resolution immediately — status changes, re-queues, PR
 *  actions — and writes the audit row. */
export const resolveIssue = (id: string, body: ResolveIssue): Promise<ResolvedIssue> =>
  api.post<ResolvedIssue>(`/api/admin/issues/${encodeURIComponent(id)}/resolve`, body);

/** The raw bundle behind an upload, for the "Open raw bundle" link. A URL,
 *  not a fetch: the browser opens it in a tab and renders the JSON itself. */
export const uploadBundleUrl = (id: string): string =>
  `/api/admin/uploads/${encodeURIComponent(id)}/bundle`;

export const adminLogs = (query?: AdminLogQuery): Promise<AdminLogPage> =>
  api.get<AdminLogPage>('/api/admin/logs', query as Query);

/** Who decided what. Every admin mutation writes one row; this reads them
 *  back, newest first, with the actor named and the documents parsed. */
export const adminAudit = (query?: AdminAuditQuery): Promise<AdminAuditPage> =>
  api.get<AdminAuditPage>('/api/admin/audit', query as Query);

/** What happened, newest first: runs, surveys, decisions, pull requests,
 *  issues, publications, edits — read from the tables that hold each. */
export const adminActivity = (query?: ActivityQuery): Promise<ActivityPage> =>
  api.get<ActivityPage>('/api/admin/activity', query as Query);

export const adminSettings = (): Promise<AdminSettings> =>
  api.get<AdminSettings>('/api/admin/settings');

/** Toggles are local until Apply, so this sends only what changed. All or
 *  nothing: one refused key leaves every other key unwritten, and the reply
 *  names only what actually moved. */
export const putAdminSettings = (settings: Partial<ServerSettings>): Promise<SettingsWritten> =>
  api.put<SettingsWritten>('/api/admin/settings', settings);

export const rotateServiceKey = (): Promise<RotatedServiceKey> =>
  api.post<RotatedServiceKey>('/api/admin/service-key/rotate');

/** Every account's sessions, with the storage each account holds riding
 *  along so the screen can say whose the bytes are without a second call. */
export const adminSessions = (query?: AdminSessionQuery): Promise<AdminSessionPage> =>
  api.get<AdminSessionPage>('/api/admin/sessions', query as Query);

/** Removes one session, its laps and their R2 objects. The bucket is asked
 *  first and allowed to fail; the reply says whether it did. */
export const purgeSession = (id: string): Promise<PurgedSession> =>
  api.del<PurgedSession>(`/api/admin/sessions/${encodeURIComponent(id)}`);

/** Who is on air, as the admin Live screen reads it: every stream row, with
 *  a stale one still listed but not counted as a driver. */
export const adminLive = (): Promise<AdminLiveListing> =>
  api.get<AdminLiveListing>('/api/admin/live');

/** Ends somebody's stream. Keyed by user id, which is what the live table
 *  stores — not the display name in the header. */
export const kickLive = (userId: string, reason?: string): Promise<KickedStream> =>
  api.post<KickedStream>(
    `/api/admin/live/${encodeURIComponent(userId)}/kick`,
    reason ? { reason } : {},
  );

export const adminCosts = (): Promise<AdminCosts> => api.get<AdminCosts>('/api/admin/costs');

// ── the account, read off the handlers ────────────────────────────────────

// Imported here rather than in the list at the top of the file: this section
// was added after that list was settled, and keeping its names together with
// it makes the pairing of reply shape to endpoint readable in one place.
import type {
  Account,
  MyUploadPage,
  NewToken,
  ServerCapabilities,
  SignInProvider,
  TokenPage,
  User,
} from '@/lib/types';

/**
 * The profile's endpoints, typed against what `src/routes/me.ts`,
 * `src/routes/tokens.ts` and `src/routes/public.ts` actually answer rather
 * than against the spec's endpoint table — see the note above `Account` in
 * `lib/types.ts`. Each one unwraps the handler's named key, so a screen
 * receives the thing it asked for and not the envelope it arrived in.
 */

export const getAccount = (): Promise<Account> => api.get<Account>('/api/me');

/** Renames the account, or changes whether its live stream is public. The
 *  reply is the account row alone, so a caller that draws the header from the
 *  session refreshes it afterwards. */
export const updateAccount = async (body: UpdateMe): Promise<User> =>
  (await api.patch<{ user: User }>('/api/me', body)).user;

/** Revokes every token and anonymises the identities. Source ids stay bound,
 *  so nobody can re-claim them, and merged survey data stays in the map. */
export const deleteAccount = (): Promise<{ deleted: boolean; deleted_at: string }> =>
  api.del<{ deleted: boolean; deleted_at: string }>('/api/me');

/** The providers this server will actually complete a handshake with. */
export const listSignInProviders = async (): Promise<SignInProvider[]> =>
  (await api.get<{ providers: SignInProvider[] }>('/api/providers')).providers;

export const listMyUploads = (query?: UploadQuery): Promise<MyUploadPage> =>
  api.get<MyUploadPage>('/api/uploads', query as Query);

export const listAccountTokens = (): Promise<TokenPage> => api.get<TokenPage>('/api/tokens');

/** The one reply with a secret in it. Show it once and let it go. */
export const issueToken = (body: CreateToken): Promise<NewToken> =>
  api.post<NewToken>('/api/tokens', body);

/** Answers the token's metadata, revoked; a token already revoked answers the
 *  same thing rather than an error. */
export const revokeAccountToken = async (id: string): Promise<TokenSummary> =>
  (await api.del<{ token: TokenSummary }>(`/api/tokens/${encodeURIComponent(id)}`)).token;

export const listInstallations = async (): Promise<Source[]> =>
  (await api.get<{ sources: Source[] }>('/api/sources')).sources;

/** Display only: the binding is what counts, and it never moves. */
export const renameInstallation = async (sourceId: string, label: string): Promise<Source> =>
  (
    await api.patch<{ source: Source }>(`/api/sources/${encodeURIComponent(sourceId)}`, { label })
  ).source;

export const readCapabilities = (): Promise<ServerCapabilities> =>
  api.get<ServerCapabilities>('/v1/capabilities');
